import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, finalize, filter } from 'rxjs/operators';
import { IInfoCard } from 'src/app/models/api/integration.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { InternalNavigationTriggerModel } from 'src/app/models/navigation/internalNavigationTrigger.model';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { AdapterDialogComponent } from 'src/app/components/adapter-dialog/adapter-dialog.component';
import {
  ConfirmData,
  ConfirmationDialogComponent,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {
  AdaptersHttpService,
  AdapterResponse,
} from 'src/app/services/httpServices/adaptersHttpService/adapters-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { formatFromDate } from 'src/app/common/datetime_utility';

@Component({
  selector: 'app-adapters',
  templateUrl: './adapters.component.html',
  styleUrls: ['./adapters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdaptersComponent implements OnInit {
  public readonly PERMISSIONS = PERMISSIONS;
  public adaptersCards$!: Observable<IInfoCard[]>;
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public maxAdapters = 200;
  public numOfAdaptersPerRow = 3;

  public readonly permissionsService = inject(PermissionsService);

  private readonly _dialogRef = inject(MatDialog);
  private readonly _adaptersHttpService = inject(AdaptersHttpService);
  private readonly _logger = inject(LoggerService);
  private readonly _notificationsService = inject(NotificationsService);

  ngOnInit(): void {
    this._loadAdapters();
  }

  private _loadAdapters(): void {
    this.isLoading$.next(true);

    this.adaptersCards$ = this._adaptersHttpService.getAllAdapters$().pipe(
      map((adapters: AdapterResponse[]) => this._mapAdaptersToCards(adapters)),
      catchError((error) => {
        this._logger.error('Failed to load adapters:', error);
        return of([]);
      }),
      finalize(() => this.isLoading$.next(false))
    );
  }

  public addAdapter(): void {
    const dialogRef = this._dialogRef.open(AdapterDialogComponent, {
      width: '640px',
      maxWidth: '90vw',
      disableClose: true,
      data: { isEditMode: false },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh adapters list
        this._loadAdapters();
      }
    });
  }

  public onCardActionClicked(event: { card: IInfoCard; action: MenuAction }): void {
    const { card, action } = event;
    const adapterId = card.cardId;

    switch (action.name) {
      case 'edit':
        this._editAdapter(adapterId);
        break;
      case 'delete':
        this._deleteAdapter(adapterId, card.primaryCardTitle);
        break;
      default:
        this._logger.warn(`Unknown action: ${action.name}`);
    }
  }

  public get disabledAddAdapterButtonReason(): string {
    if (!this.permissionsService.userHasPermission(PERMISSIONS.INTEGRATION_OPERATION)) {
      return 'You do not have sufficient permissions to add an adapter';
    }
    return 'Your tenant has reached the maximum adapters quota';
  }

  private _mapAdaptersToCards(adapters: AdapterResponse[]): IInfoCard[] {
    return adapters.map((adapter) => {
      const isConnected = adapter.enabled;
      const isHealthy = adapter.connectionStatus?.status === 'healthy';

      // If enabled, check health status; otherwise show as disconnected
      const statusLabel = isConnected
        ? isHealthy
          ? StatusLabelEnum.connected
          : StatusLabelEnum.failed
        : StatusLabelEnum.disconnected;

      const getPlatformDisplayName = (adapterType: string): string => {
        switch (adapterType) {
          case 'splunk':
            return 'Splunk';
          case 'sentinel':
            return 'Microsoft Sentinel';
          default:
            return adapterType.charAt(0).toUpperCase() + adapterType.slice(1);
        }
      };

      const platformName = getPlatformDisplayName(adapter.config.adapterType);

      const formatStatusTooltip = (status: string | undefined): string => {
        if (!status) return 'Connection failed';
        // Convert camelCase to Title Case (e.g., "networkTimeout" -> "Network Timeout")
        return status
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      };

      const leftFields: ICardField[] = [
        {
          key: 'Integration type',
          value: 'Outbound',
          iconPath: 'assets/images/common/outbound-icon.svg',
          isChipStyle: true,
        },
        {
          key: 'Platform',
          value: platformName,
        },
        {
          key: 'Environment',
          value: '-',
        },
        {
          key: 'Integration method',
          value: '-',
        },
      ];

      // Extract email from "provider|email@domain.com" format
      const extractEmail = (value: string | undefined): string => {
        if (!value) return '-';
        const parts = value.split('|');
        return parts.length > 1 ? parts[parts.length - 1] : value;
      };

      const rightFields: ICardField[] = [
        {
          key: 'Created at',
          value: formatFromDate(adapter.createdAt),
        },
        {
          key: 'Created by',
          value: extractEmail(adapter.createdBy),
        },
        {
          key: 'Last changed',
          value: formatFromDate(adapter.lastUpdatedAt || adapter.createdAt),
        },
        {
          key: 'Last changed by',
          value: extractEmail(adapter.lastUpdatedBy),
        },
      ];

      const menuActions: MenuAction[] = [
        {
          name: 'edit',
          displayNameLabel: 'adaptersPage.edit',
          disabled: false,
          shouldOpenDialog: true,
        },
        {
          name: 'delete',
          displayNameLabel: 'adaptersPage.delete',
          disabled: false,
        },
      ];

      const getPlatformLogoPath = (adapterType: string): string | undefined => {
        switch (adapterType) {
          case 'splunk':
            return 'assets/images/logo/splunk-logo.svg';
          case 'sentinel':
            return 'assets/images/logo/microsoft-sentinel-logo.svg';
          default:
            return undefined;
        }
      };

      const platformLogoPath = getPlatformLogoPath(adapter.config.adapterType);

      return {
        cardId: adapter.id,
        primaryCardTitle: adapter.name,
        secondaryCardTitle: 'ID:',
        secondaryCardValue: adapter.id,
        secondaryCardTooltipText: '',
        statusLabel: statusLabel,
        statusTooltip: isConnected && !isHealthy ? formatStatusTooltip(adapter.connectionStatus?.status) : '',
        menuActions: menuActions,
        leftCardFields: leftFields,
        rightCardFields: rightFields,
        internalNavigation: {} as InternalNavigationTriggerModel,
        platformLogoPath: platformLogoPath,
        cardType: 'adapter',
      };
    });
  }

  private _editAdapter(adapterId: string): void {
    const dialogRef = this._dialogRef.open(AdapterDialogComponent, {
      width: '640px',
      maxWidth: '90vw',
      disableClose: true,
      data: { isEditMode: true, adapterId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._loadAdapters();
      }
    });
  }

  private _deleteAdapter(adapterId: string, adapterName?: string): void {
    const confirmData: ConfirmData = {
      title: 'Delete adapter?',
      content: `Are you sure you want to delete adapter <strong>${adapterName || adapterId}</strong>?`,
      positiveOptionText: 'Delete',
      negativeOptionText: 'Cancel',
      icon: 'trash-2',
    };

    const dialogRef = this._dialogRef.open(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(filter((confirmed) => confirmed === true))
      .subscribe(() => {
        this._adaptersHttpService.deleteAdapter$(adapterId).subscribe({
          next: () => {
            this._logger.info('Adapter deleted successfully');
            this._loadAdapters();
          },
          error: (error: HttpErrorResponse) => {
            this._logger.error('Failed to delete adapter:', error);
            if (error.status === 409) {
              this._notificationsService.error(error.error?.detail || 'Cannot delete adapter: it is in use');
            } else {
              this._notificationsService.error('Failed to delete adapter');
            }
          },
        });
      });
  }
}
